import {
  ElementNode,
  setDOMUnmanaged,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from "lexical";

import {
  DISCOVER_GRID_COUNT,
  PRODUCT_CONTEXT_CLOSE_MARKER,
  productContextOpenMarker,
} from "../productContexts";

import { PRODUCT_GRID_MODELS } from "@/features/EmailEditor/plugins/productGridConstants";

export type SerializedProductContextNode = Spread<
  { model: string; grid: number },
  SerializedElementNode
>;

export const PRODUCT_CONTEXT_CLASS = "lexical-product-context";
export const PRODUCT_CONTEXT_HEADER_CLASS = "lexical-product-context-header";
export const PRODUCT_CONTEXT_ROW_CLASS = "lexical-product-context-row";
export const PRODUCT_CONTEXT_BODY_CLASS = "lexical-product-context-body";
export const PRODUCT_MIRRORS_CLASS = "lexical-product-mirrors";
export const PRODUCT_MIRRORS_ON_CLASS = "lexical-product-mirrors-on";
export const PRODUCT_MIRROR_CLASS = "lexical-product-mirror";
export const PRODUCT_MIRROR_OPEN_CLASS = "lexical-product-mirror-open";
export const PRODUCT_MIRROR_HEADER_CLASS = "lexical-product-mirror-header";
export const PRODUCT_MIRROR_BODY_CLASS = "lexical-product-mirror-body";

export const DISCOVER_TOOLTIP =
  "Discover sources items from 5 different models. Each Discover section sends different items.";

export function productModelLabel(model: string): string {
  return (
    PRODUCT_GRID_MODELS.find((option) => option.value === model)?.label ?? model
  );
}

/** Header text: discover names its set, every other model just its label. */
export function productContextLabel(model: string, grid: number): string {
  const label = productModelLabel(model);
  return model === "discover"
    ? `${label} ${grid + 1} of ${DISCOVER_GRID_COUNT}`
    : label;
}

/**
 * Read-only rows for sets 2–5, filled by `ProductMirrorPlugin` from the
 * authored blocks. Static chrome, so the node builds it once.
 */
function buildMirrors(): HTMLElement {
  const mirrors = document.createElement("div");
  mirrors.className = PRODUCT_MIRRORS_CLASS;
  mirrors.setAttribute("contenteditable", "false");
  for (let grid = 1; grid < DISCOVER_GRID_COUNT; grid += 1) {
    const row = document.createElement("div");
    row.className = PRODUCT_MIRROR_CLASS;
    const header = document.createElement("span");
    header.className = PRODUCT_MIRROR_HEADER_CLASS;
    header.setAttribute("role", "button");
    header.setAttribute("tabindex", "0");
    header.setAttribute("aria-expanded", "false");
    header.setAttribute("data-tip", DISCOVER_TOOLTIP);
    header.innerHTML = `<span class="lexical-product-mirror-chevron">▸</span><span>${productContextLabel("discover", grid)}</span>`;
    const body = document.createElement("div");
    body.className = PRODUCT_MIRROR_BODY_CLASS;
    row.append(header, body);
    mirrors.appendChild(row);
  }
  return mirrors;
}

/**
 * A section bound to one product model, holding copy and product blocks. The
 * whole section drops when the model has no items, so a headline can't outlive
 * the products it introduces.
 *
 * The model is authored here rather than derived from the tile, which is why —
 * unlike the block marker — it appears in the serialised marker.
 */
export class ProductContextNode extends ElementNode {
  __model: string;
  /** Discover grid ordinal — positional, maintained by the reindex pass. */
  __grid: number;

  static getType(): string {
    return "product-context";
  }

  static clone(node: ProductContextNode): ProductContextNode {
    return new ProductContextNode(node.__model, node.__grid, node.__key);
  }

  static importJSON(
    serializedNode: SerializedProductContextNode,
  ): ProductContextNode {
    return new ProductContextNode(
      serializedNode.model ?? "",
      serializedNode.grid ?? 0,
    ).updateFromJSON(serializedNode);
  }

  constructor(model: string, grid = 0, key?: NodeKey) {
    super(key);
    this.__model = model;
    this.__grid = grid;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedProductContextNode>,
  ): this {
    return super.updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedProductContextNode {
    return { ...super.exportJSON(), model: this.__model, grid: this.__grid };
  }

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = PRODUCT_CONTEXT_CLASS;
    div.setAttribute("data-product-model", this.__model);

    const header = document.createElement("span");
    header.className = PRODUCT_CONTEXT_HEADER_CLASS;
    header.setAttribute("contenteditable", "false");
    header.setAttribute("role", "button");
    header.setAttribute("tabindex", "0");
    header.setAttribute(
      "aria-label",
      `${productModelLabel(this.__model)} product context options`,
    );
    if (this.__model === "discover")
      header.setAttribute("data-tip", DISCOVER_TOOLTIP);

    const label = document.createElement("span");
    label.className = "lexical-product-header-label";
    label.textContent = productContextLabel(this.__model, this.__grid);

    const remove = document.createElement("span");
    remove.className = "lexical-product-delete";
    remove.setAttribute("role", "button");
    remove.setAttribute("aria-label", "Delete section");
    remove.textContent = "✕";

    header.append(label, remove);

    const row = document.createElement("div");
    row.className = PRODUCT_CONTEXT_ROW_CLASS;
    row.setAttribute("contenteditable", "false");
    row.append(header);

    // Lexical's children go here rather than straight into the section, so the
    // chrome around them is never part of the managed range.
    const body = document.createElement("div");
    body.className = PRODUCT_CONTEXT_BODY_CLASS;

    const mirrors = buildMirrors();
    // Chrome Lexical must neither reconcile nor "repair": the mirror plugin
    // rewrites the mirror bodies and chevrons at runtime.
    setDOMUnmanaged(row);
    setDOMUnmanaged(mirrors);
    div.append(row, body, mirrors);

    return div;
  }

  /**
   * Points the slot at the children host, Lexical's documented wrap pattern.
   * The header row and the mirrors are siblings of that host rather than of
   * the managed children, so there is no boundary to maintain and an appended
   * child cannot land past the mirrors.
   */
  getDOMSlot(element: HTMLElement) {
    const body = element.querySelector<HTMLElement>(
      `:scope > .${PRODUCT_CONTEXT_BODY_CLASS}`,
    );
    return super.getDOMSlot(element).withElement(body ?? element);
  }

  updateDOM(prevNode: ProductContextNode, dom: HTMLElement): boolean {
    if (prevNode.__model === this.__model && prevNode.__grid === this.__grid) {
      return false;
    }
    dom.setAttribute("data-product-model", this.__model);
    const header = dom.querySelector(`.${PRODUCT_CONTEXT_HEADER_CLASS}`);
    if (header instanceof HTMLElement) {
      header.setAttribute(
        "aria-label",
        `${productModelLabel(this.__model)} product context options`,
      );
      if (this.__model === "discover") {
        header.setAttribute("data-tip", DISCOVER_TOOLTIP);
      } else {
        header.removeAttribute("data-tip");
      }
      const label = header.firstChild;
      if (label instanceof HTMLElement) {
        label.textContent = productContextLabel(this.__model, this.__grid);
      }
    }
    return false;
  }

  /**
   * Children are paragraphs and product blocks, each already whole lines, so
   * they join with `\n` — the marker pair then wraps the section.
   */
  getTextContent(): string {
    const inner = this.getChildren()
      .map((child) => child.getTextContent())
      .join("\n");
    return [
      productContextOpenMarker(this.__model, this.__grid),
      inner,
      PRODUCT_CONTEXT_CLOSE_MARKER,
    ].join("\n");
  }

  getModel(): string {
    return this.getLatest().__model;
  }

  setModel(model: string): this {
    const writable = this.getWritable();
    writable.__model = model;
    return writable;
  }

  getGrid(): number {
    return this.getLatest().__grid;
  }

  setGrid(grid: number): this {
    const writable = this.getWritable();
    writable.__grid = grid;
    return writable;
  }

  /**
   * Backspace never dissolves the section — that would orphan product chips
   * outside any context. The structural two-step in `ProductBlockPlugin`
   * (select the wrapper, then delete) owns its lifecycle.
   */
  collapseAtStart(): boolean {
    return true;
  }

  canBeEmpty(): boolean {
    return false;
  }

  isInline(): boolean {
    return false;
  }

  isShadowRoot(): boolean {
    return false;
  }
}

export function $createProductContextNode(model: string): ProductContextNode {
  return new ProductContextNode(model);
}

export function $isProductContextNode(
  node: LexicalNode | null | undefined,
): node is ProductContextNode {
  return node instanceof ProductContextNode;
}
